// project import
import { dispatch } from "store";

// third-party
import { createSlice } from "@reduxjs/toolkit";
import { firestore } from "contexts/FirebaseContext";
import { collection, doc, getDoc, increment, onSnapshot, updateDoc } from "firebase/firestore";

const initialState = {
  isOpen: false,
  isCustomerOpen: false,
  open: false,
  dataList: [],
  data: null,
  invoiceNumber: null,
  error: null,
  alertPopup: false,
  monthlyKpi: 0, // <-- NEW STATE FIELD
  outreachData: [],
};

const data = createSlice({
  name: "data",
  initialState,
  reducers: {
    // review data popup
    reviewLeadPopup(state, action) {
      state.isOpen = action.payload.isOpen;
    },

    // is customer open
    customerPopup(state, action) {
      state.isCustomerOpen = action.payload.isCustomerOpen;
    },

    // handler customer form popup
    toggleCustomerPopup(state, action) {
      state.open = action.payload.open;
    },

    hasError(state, action) {
      state.error = action.payload.error;
    },

    // get all data data
    getIncome(state, action) {
      state.dataList = action.payload;
    },

    // get invoice number
    getInvoiceNumber(state, action) {
      state.invoiceNumber = action.payload;
    },

    // get data details
    getSingleList(state, action) {
      state.data = action.payload;
    },

    // get monthly KPI value <-- NEW REDUCER
    getMonthlyKpi(state, action) {
      state.monthlyKpi = action.payload;
    },

    // Add to your data slice reducers
    getOutreachData(state, action) {
      state.outreachData = action.payload;
    },

    // create data
    createData(state, action) {
      state.dataList = [...state.dataList, action.payload];
    },

    // update data
    UpdateData(state, action) {
      const { NewData } = action.payload;
      const DataUpdate = state.dataList.map((item) => {
        if (item.id === NewData.id) {
          return NewData;
        }
        return item;
      });
      state.dataList = DataUpdate;
    },

    // delete data
    deleteData(state, action) {
      const { invoiceId } = action.payload;
      const deleteData = state.dataList.filter((data) => data.id !== invoiceId);
      state.dataList = deleteData;
    },

    //alert popup
    alertPopupToggle(state, action) {
      state.alertPopup = action.payload.alertToggle;
    },
  },
});

export default data.reducer;

export const {
  reviewLeadPopup,
  customerPopup,
  toggleCustomerPopup,
  selectCountry,
  getIncome,
  alertPopupToggle,
  getMonthlyKpi, // <-- NEW EXPORT
} = data.actions;

export const subscribeToDataRevenue = () => {
  const unsubscribeFunction = onSnapshot(
    collection(firestore, `data/revenue/data_${new Date().getFullYear()}`),
    (snapshot) => {
      const d = snapshot.docs.map((doc) => doc.data());
      dispatch(data.actions.getIncome(d));
    }
  );

  return unsubscribeFunction;
};

export const subscribeToMonthlyKpi = () => {
  const currentDate = new Date();
  const year = currentDate.getFullYear();

  // FIX: Force the locale to 'en-US' to ensure the document ID is always English (e.g., 'december')
  const monthName = currentDate.toLocaleString("en-US", { month: "long" }).toLowerCase();

  // Doc path: data/kpi/data_YYYY/monthName
  const docRef = doc(firestore, `data/kpi/data_${year}`, monthName);

  const unsubscribeFunction = onSnapshot(docRef, (docSnap) => {
    let value = 0;
    if (docSnap.exists()) {
      value = docSnap.data().value || 0;
    }
    dispatch(data.actions.getMonthlyKpi(value));
  });

  return unsubscribeFunction;
};

export const getDataInvoiceNumber = () => {
  return async (dispatch) => {
    try {
      // get invoice document reference from collection data
      const documentReference = doc(firestore, "data/invoice");
      // get field value of invoiceNumber from documentReference
      const documentSnapshot = await getDoc(documentReference);
      const number = documentSnapshot.data().invoiceNumber;
      // dispatch value of field invoiceNumber
      dispatch(data.actions.getInvoiceNumber(number));
    } catch (error) {
      console.error(error);
      dispatch(data.actions.hasError(error));
    }
  };
};

// Add this new subscription function at the bottom of data.js
export const subscribeToOutreachYearly = () => {
  const year = new Date().getFullYear();
  // Reference to the sub-collection for the year
  const colRef = collection(firestore, `data/outreach/data_${year}`);

  const unsubscribeFunction = onSnapshot(colRef, (snapshot) => {
    // This will get all months (january, february, etc.)
    const yearData = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    dispatch(data.actions.getOutreachData(yearData));
  });

  return unsubscribeFunction;
};

// Add this new subscription function to data.js
export const subscribeToOutreachMonthly = () => {
  const currentDate = new Date();
  const year = currentDate.getFullYear();
  // Ensure the month name is lowercase to match Firestore document IDs
  const monthName = currentDate.toLocaleString("en-US", { month: "long" }).toLowerCase();

  // Reference directly to the specific month document
  const docRef = doc(firestore, `data/outreach/data_${year}`, monthName);

  const unsubscribeFunction = onSnapshot(docRef, (docSnap) => {
    let monthData = {};
    if (docSnap.exists()) {
      monthData = docSnap.data();
    }
    // We send an array with one object to maintain compatibility with the chart's loop
    dispatch(data.actions.getOutreachData([monthData]));
  });

  return unsubscribeFunction;
};

export const incrementCallStat = async (userId, statType) => {
  const currentDate = new Date();
  const year = currentDate.getFullYear();
  const monthName = currentDate.toLocaleString("en-US", { month: "long" }).toLowerCase();

  // 1. Format the date key: YYYY-MM-DD
  const dateKey = currentDate.toISOString().split("T")[0];

  // 2. Map UID to user suffix
  const userSuffix = userId === "wDwxNdcPgYempbC4aDXciSDJ82f2" ? "eeli" : "joose";

  // 3. Create the nested path string: "2026-02-23.answered_eeli"
  const fieldPath = `${dateKey}.${statType}_${userSuffix}`;

  const docRef = doc(firestore, `data/outreach/data_${year}`, monthName);

  try {
    // updateDoc with dot notation handles the nested object update atomically
    await updateDoc(docRef, {
      [fieldPath]: increment(1),
    });
  } catch (error) {
    console.error("Error updating nested call stats:", error);
  }
};

/**
 * Updates call stats for a specific day within the monthly document.
 * @param {string} userId - The UID of the current user.
 * @param {string} statType - 'answered' or 'no_answer'.
 * @param {number} value - The amount to change (1 or -1).
 */
export const updateCallStat = async (userId, statType, value) => {
  const currentDate = new Date();
  const year = currentDate.getFullYear();
  const monthName = currentDate.toLocaleString("en-US", { month: "long" }).toLowerCase();

  // Format the date key: YYYY-MM-DD
  const dateKey = currentDate.toISOString().split("T")[0];

  // Map UID to user suffix
  const userSuffix = userId === "wDwxNdcPgYempbC4aDXciSDJ82f2" ? "eeli" : "joose";

  // Construct path: "2026-02-23.answered_eeli"
  const fieldPath = `${dateKey}.${statType}_${userSuffix}`;

  const docRef = doc(firestore, `data/outreach/data_${year}`, monthName);

  try {
    await updateDoc(docRef, {
      [fieldPath]: increment(value),
    });
  } catch (error) {
    console.error("Error updating nested call stats:", error);
  }
};
